import { ActivityCategory, ActivityTargetView } from "@sp-crm/core";

const roundDown = (value: number): number => Math.floor(value);

const categoryOrder: ActivityCategory[] = ["visit", "call", "sms", "email", "other"];

const categoryNouns: Record<ActivityCategory, { singular: string; plural: string }> = {
    visit: { singular: "visit", plural: "visits" },
    call: { singular: "call", plural: "calls" },
    sms: { singular: "text", plural: "texts" },
    email: { singular: "email", plural: "emails" },
    other: { singular: "activity", plural: "activities" },
};

export interface TierTouchpointVolumeGroup {
    targets: ActivityTargetView[];
    volume: number;
}

const categoryOrderIndex = (targets: ActivityTargetView[]): number => {
    const indexes = targets
        .map(target => categoryOrder.indexOf(target.category))
        .filter(index => index >= 0);
    return indexes.length === 0 ? categoryOrder.length : Math.min(...indexes);
};

export const scaleTierVolumeGroups = (
    groups: TierTouchpointVolumeGroup[],
    fraction: number,
): TierTouchpointVolumeGroup[] =>
    groups.map(group => ({ targets: group.targets, volume: group.volume * fraction }));

const singularTargetNoun = (target: ActivityTargetView): string =>
    target.kind === "taskType" ? target.label : categoryNouns[target.category].singular;

const pluralTargetNoun = (target: ActivityTargetView): string => {
    if (target.kind === "taskType") {
        return target.label.endsWith("s") ? target.label : `${target.label}s`;
    }
    return categoryNouns[target.category].plural;
};

export const formatTierVolume = (groups: TierTouchpointVolumeGroup[]): string => {
    const nonZeroGroups = groups.filter(group => group.volume > 0);
    if (nonZeroGroups.length === 0) {
        return "no cadence configured";
    }
    const sortedGroups = [...nonZeroGroups].sort(
        (a, b) => categoryOrderIndex(a.targets) - categoryOrderIndex(b.targets),
    );
    const parts = sortedGroups.map(group => {
        const count = roundDown(group.volume);
        const sortedTargets = [...group.targets].sort(
            (a, b) => categoryOrderIndex([a]) - categoryOrderIndex([b]),
        );
        const labels = sortedTargets.map(target =>
            count === 1 ? singularTargetNoun(target) : pluralTargetNoun(target),
        );
        return `${count} ${labels.join("/")}`;
    });
    return parts.join(", ");
};
